import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import {inject, injectable} from 'inversify';
import {from, map, Observable, take} from 'rxjs';
import {Auth} from '../auth';
import {GlobalContainerTypes} from '../../types';
import {Http, RequestConfig} from './http.types';
import {notificationsManager} from '@innowise-group/ui-kit';
import {Language} from '../language';
import {
  getApiErrorCode,
  WORKSPACE_CROSSES_WALL_ERROR_CODE,
  WORKSPACE_OUTSIDE_ROOM_ERROR_CODE,
} from '../../utilities/api-error.utility';

@injectable()
class HttpService implements Http {
  public static readonly type = GlobalContainerTypes.Http;

  @inject('Auth') private auth: Auth;
  @inject(GlobalContainerTypes.Language) private languageService: Language;
  private axiosInstance: AxiosInstance;

  public constructor() {
    this.axiosInstance = axios.create();
    this.applyInterceptors();
  }

  public get originUrl(): string {
    return process.env.REACT_APP_API_BASE_URL || window.location.origin;
  }

  private get languageUrl(): string {
    const language = this.languageService.getAppLanguage();
    return language === 'ru' ? '' : '/en';
  }

  public get hrmRequestConfig(): RequestConfig {
    return {baseURL: `${process.env.REACT_APP_HRM_API_BASE_URL || window.location.origin}/api`};
  }

  public get chatbotRequestConfig(): RequestConfig {
    return {baseURL: process.env.REACT_APP_CHATBOT_API_BASE_URL || window.location.origin, ignoreInterceptors: true};
  }

  private applyInterceptors(): void {
    this.axiosInstance.interceptors.request.use(async (config: RequestConfig) => {
      if (config.ignoreInterceptors) return config;

      if (!config.baseURL) {
        config.baseURL = `${this.originUrl}${this.languageUrl}/api/v1`;
      }
      await this.auth.updateToken();
      const accessToken = this.auth.authState.accessToken;
      if (!accessToken) {
        // eslint-disable-next-line no-console
        console.error('[auth] No access token after updateToken; request will 401', config.url);
      }
      config.headers = {
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
        'Content-Type': 'application/json',
      };
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error.response && !axios.isCancel(error)) {
          notificationsManager.error({title: 'networkError', subtitle: 'networkError'});
        } else {
          const {data, status, statusText} = error.response;
          const errorCode = getApiErrorCode(error);

          if (status >= 400) {
            if (errorCode === WORKSPACE_OUTSIDE_ROOM_ERROR_CODE || errorCode === WORKSPACE_CROSSES_WALL_ERROR_CODE) {
              notificationsManager.error({
                title: 'statusTextError',
                subtitle:
                  errorCode === WORKSPACE_OUTSIDE_ROOM_ERROR_CODE ? 'workspaceOutsideRoom' : 'workspaceCrossesWall',
                statusCode: `${statusText}`,
              });
            } else {
              notificationsManager.error({
                title: `statusTextError`,
                subtitle:
                  typeof data === 'object'
                    ? Object.entries(data).reduce((accumulator, [key, value]) => accumulator + `${key}: ${value}`, '')
                    : 'unknownError',
                statusCode: `${statusText}`,
              });
            }
          }
          return Promise.reject(error);
        }
      },
    );

    // this.axiosInstance.interceptors.response.use(
    //   (response) => {
    //     return response.data;
    //   },
    //   async function (error) {
    //     if (error.response.status === 401 && !error.config.retry) {
    //       error.config.retry = true;
    //       const refreshToken = refreshHeader();
    //       const newToken = await refreshToken(refresh_token);
    //       error.response.config.headers['Authorization'] = 'Bearer ' + newToken;
    //       return axiosApiInstance(error.response.config);
    //     }
    //     return Promise.reject(error);
    //   },
    // );
  }

  public GET<T>(url: string, config?: AxiosRequestConfig): Observable<T> {
    return from(this.axiosInstance.get<T>(url, config)).pipe(
      take(1),
      map((response) => response.data),
    );
  }

  public POST<T, G = {}>(url: string, form: G, config?: AxiosRequestConfig): Observable<T> {
    return from(this.axiosInstance.post<T>(url, form, config)).pipe(
      take(1),
      map((response) => response.data),
    );
  }

  public PUT<T, G = {}>(url: string, form: G): Observable<T> {
    return from(this.axiosInstance.put<T>(url, form)).pipe(
      take(1),
      map((response) => response.data),
    );
  }

  public DELETE<T, G = {}>(url: string, form: G): Observable<T> {
    return from(this.axiosInstance.delete<T>(url, form)).pipe(
      take(1),
      map((response) => response.data),
    );
  }

  public PATCH<T, G = {}>(url: string, form: G): Observable<T> {
    return from(this.axiosInstance.patch<T>(url, form)).pipe(
      take(1),
      map((response) => response.data),
    );
  }
}

export default HttpService;
